import React, { forwardRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import SelectComponent from 'react-select'
import classnames from 'classnames'
import { useTheme } from 'react-jss'

import SelectOption from './SelectOption'
import SelectSingleValue from './SelectSingleValue'
import SelectMultiValueLabel from './SelectMultiValueLabel'
import SelectDropdownIndicator from './SelectDropdownIndicator'
import SelectValueContainer from './SelectValueContainer'
import SelectMenu from './SelectMenu'
import SelectIndicatorsContainer from './SelectIndicatorsContainer'
import SelectPlaceholder from './SelectPlaceholder'
import MultiLevelSelectMenuList from '../MultiLevelSelect/MultiLevelSelectMenuList'

import { useStyles, getSelectStyles } from './styles'

const Select = forwardRef(
  (
    {
      className,
      selectClassName,
      portaled,
      value,
      formatValue = true,
      defaultValue = null,
      options,
      showIndicator = true,
      customMultiValueLabel,
      optionWithTag,
      CustomOption,
      CustomValueContainer,
      customComponentsReset,
      isIconSelect,
      placeholder = 'Select...',
      isDisabled,
      children,
      ...props
    },
    ref
  ) => {
    const theme = useTheme()
    const classes = useStyles({ isDisabled })

    const customComponents = {
      ValueContainer: CustomValueContainer ? CustomValueContainer : SelectValueContainer,
      MultiValueLabel: SelectMultiValueLabel,
      DropdownIndicator: showIndicator ? SelectDropdownIndicator : null,
      ClearIndicator: null,
      IndicatorsContainer: SelectIndicatorsContainer,
      Placeholder: SelectPlaceholder,
      Option: CustomOption ? CustomOption : SelectOption,
      SingleValue: SelectSingleValue,
      Menu: SelectMenu,
      MenuList: MultiLevelSelectMenuList,
      ...customComponentsReset
    }

    const getFormattedValue = useCallback(
      value => {
        // react-select operates with object value
        // to be able represent selected option it should be converted to object
        if (value || value === 0) {
          let selectedValue = options.find(option => option.value.toString() === value.toString())
          return selectedValue || ''
        } else {
          return ''
        }
      },
      [options]
    )

    const componentProps = {
      defaultValue,
      options,
      isIconSelect,
      isDisabled,
      showIndicator,
      value: (formatValue ? getFormattedValue(value) : value) || defaultValue,
      styles: getSelectStyles({ ...props, isIconSelect, isDisabled, theme }),
      menuPortalTarget: portaled && document && document.body,
      components: customComponents,
      placeholder,
      ...props
    }

    return (
      <div className={classnames(classes.select, className)}>
        {children ? (
          React.cloneElement(children, {
            ...componentProps,
            // when AsyncPaginate is passed to the Select component as a child and ref is needed, then it should
            // be passed as selectRef prop, default ref is not working - check the react-select-async-paginate
            selectRef: ref // workaround for the issue with the ref forwarding
          })
        ) : (
          <SelectComponent {...componentProps} ref={ref} />
        )}
      </div>
    )
  }
)

const valuePropTypeShape = {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  label: PropTypes.string
}

Select.propTypes = {
  className: PropTypes.string,
  customStyles: PropTypes.object,
  CustomDownIcon: PropTypes.node,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  portaled: PropTypes.bool,
  title: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date)
  ]),
  defaultValue: PropTypes.shape(valuePropTypeShape),
  options: PropTypes.arrayOf(PropTypes.shape(valuePropTypeShape)),
  onChange: PropTypes.func,
  showIndicator: PropTypes.bool,
  showArrowIndicator: PropTypes.bool,
  showClearInputIcon: PropTypes.bool,
  onClearInput: PropTypes.func,
  isMulti: PropTypes.bool,
  isIconSelect: PropTypes.bool,
  // styles
  bigIcon: PropTypes.bool,
  isSmall: PropTypes.bool,
  isHovered: PropTypes.bool,
  noBorder: PropTypes.bool,
  transparent: PropTypes.bool,
  menuContainerClassName: PropTypes.string,
  indicatorsClassName: PropTypes.string
}

export default Select
